import { useState, useEffect, useCallback } from 'react';

// Shared histogram bar-click behavior for the earnings list pages (desktop /earnings
// and mobile view): scroll to the date's group when the date is inside the list
// window — chasing pages via fetchNextPage until the group is loaded — and open the
// inline day panel for dates beyond the window (the list can never contain those).
export const useHistogramNavigation = ({ data, dateGroups, hasNextPage, isFetchingNextPage, fetchNextPage, histogram }) => {
  const [scrollTarget, setScrollTarget] = useState(null);
  const [panelDate, setPanelDate] = useState(null);

  const handleBarClick = useCallback((date) => {
    const windowDates = (data?.pages?.[0]?.dates || [])
      .map((d) => (d.includes('T') ? d.split('T')[0] : d));
    if (windowDates.includes(date)) {
      setPanelDate(null);
      setScrollTarget(date);
    } else {
      setScrollTarget(null);
      setPanelDate((prev) => (prev === date ? null : date));
    }
  }, [data?.pages]);

  const closePanel = useCallback(() => setPanelDate(null), []);

  // Re-runs as pages load (dateGroups grows) until the target group exists in the DOM.
  useEffect(() => {
    if (!scrollTarget) return;
    const el = document.getElementById(`earnings-group-${scrollTarget}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollTarget(null);
    } else if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    } else if (!hasNextPage) {
      setScrollTarget(null); // exhausted the list without finding it — give up quietly
    }
  }, [scrollTarget, dateGroups, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const panelBellwethers = panelDate
    ? (histogram?.find((h) => h.date === panelDate)?.bellwethers || [])
    : [];

  return { handleBarClick, panelDate, panelBellwethers, closePanel };
};
